/**
 * Carousel Config Resolver
 *
 * Computes runtime values (slideSize) from the stored carouselOptions attribute.
 * Also provides mobile-first breakpoint cascade and Embla options builder.
 */

import { breakpoints as BREAKPOINTS } from '@groundworx/foundation';

const BREAKPOINT_ORDER = Object.keys( BREAKPOINTS );

function parseBreakpointPx( value ) {
	if ( ! value ) return 0;
	return parseInt( value.replace( 'px', '' ), 10 ) || 0;
}

/**
 * Compute slideSize from perPage / fixedWidth at a single config level.
 *
 * @param {Object} opts - A single level of carouselOptions (root or breakpoint).
 * @return {Object} Config with computed slideSize added.
 */
function computeSlideSize( opts ) {
	if ( ! opts ) return {};

	const config = { ...opts };

	// perPage → slideSize (percentage)
	if ( opts.perPage !== undefined && opts.perPage > 0 ) {
		config.slideSize = `${ ( 100 / opts.perPage ).toFixed( 4 ) }%`;
	}

	// fixedWidth overrides perPage
	if ( opts.fixedWidth ) {
		config.slideSize = opts.fixedWidth;
		config.slideSizeMode = 'fixed';
	}

	return config;
}

/**
 * Resolve the full carouselOptions (with breakpoints) into runtime config.
 *
 * @param {Object} carouselOptions - The raw carouselOptions attribute value.
 * @return {Object} Runtime config with computed slideSize + breakpoints.
 */
export function resolveCarouselConfig( carouselOptions ) {
	if ( ! carouselOptions ) return {};

	const root = computeSlideSize( carouselOptions );

	if ( carouselOptions.breakpoints ) {
		root.breakpoints = {};
		for ( const [ bpName, bpOpts ] of Object.entries( carouselOptions.breakpoints ) ) {
			root.breakpoints[ bpName ] = computeSlideSize( bpOpts );
		}
	}

	return root;
}

/**
 * Get the effective value for a setting at the given viewport width.
 * Uses mobile-first cascade: starts with root value, then overrides from breakpoints.
 *
 * @param {Object} config  - Output of resolveCarouselConfig().
 * @param {string} key     - The setting key to look up.
 * @param {number} viewportWidth - Current viewport width in pixels.
 * @return {*} The effective value.
 */
export function getEffectiveValue( config, key, viewportWidth ) {
	if ( ! config ) return undefined;

	let value = config[ key ];

	if ( ! config.breakpoints ) return value;

	for ( const bpKey of BREAKPOINT_ORDER ) {
		const bpValue = BREAKPOINTS[ bpKey ];
		if ( ! bpValue ) continue;

		const bpPx = parseBreakpointPx( bpValue );
		const bpConfig = config.breakpoints[ bpKey ];

		if ( viewportWidth >= bpPx && bpConfig && bpConfig[ key ] !== undefined && bpConfig[ key ] !== null ) {
			value = bpConfig[ key ];
		}
	}

	return value;
}

/**
 * Build Embla constructor options from an effective set of config values.
 *
 * @param {Object} effective - Resolved config values for current viewport.
 * @return {Object} Embla options.
 */
export function buildEmblaOptions( effective ) {
	const loop = effective.loop ?? false;

	return {
		loop,
		align: loop ? ( effective.align ?? 'start' ) : 'start',
		slidesToScroll: effective.slidesToScroll ?? 1,
		containScroll: loop ? false : ( effective.containScroll || false ),
		duration: 20,
		skipSnaps: true,
		watchSlides: true,
		watchResize: true,
	};
}

/**
 * Get the current breakpoint key for a given viewport width.
 *
 * @param {number} viewportWidth - Viewport width in pixels.
 * @return {string} The active breakpoint key, or 'default'.
 */
export function getBreakpointKey( viewportWidth ) {
	let active = 'default';
	for ( const bpKey of BREAKPOINT_ORDER ) {
		const bpValue = BREAKPOINTS[ bpKey ];
		if ( ! bpValue ) continue;
		const bpPx = parseBreakpointPx( bpValue );
		if ( viewportWidth >= bpPx ) {
			active = bpKey;
		}
	}
	return active;
}
